import {PlacementType, Referral, ReferralConsideration, ReferralStatus, ServiceType} from './types';
import {difference} from "lodash";

export const isAssessment = (referral: Pick<Referral, 'placementType'>): boolean => [
    PlacementType.Assessment_Only,
    PlacementType.Assessment_with_Concurrent_Treatment
].includes(referral.placementType);


export const isTreatmentOrAssessmentWithTreatment = (referral: Referral): boolean => {
    return isTreatmentOnly(referral) || isAssessmentWithConcurrentTreatment(referral);
}

export const isTreatmentOnly = (referral: Pick<Referral, 'placementType'>): boolean => [
    PlacementType.Treatment_Only,
].includes(referral.placementType);

export const isAssessmentWithConcurrentTreatment = (referral: Pick<Referral, 'placementType'>): boolean => [
    PlacementType.Assessment_with_Concurrent_Treatment
].includes(referral.placementType);

export const isGroupService = (referral: Pick<Referral, 'serviceTypes'>): boolean => {
    const groupServices = [
        ServiceType.Group_ABA,
        ServiceType.Social_Skills_Group,
    ];
    return referral.serviceTypes?.some(type => groupServices.indexOf(type) !== -1) ?? false;
}

export const isClinicRequired = (referral: Pick<Referral, 'serviceTypes' | 'placementType' | 'referralConsiderations'>, forceClinic?: boolean) =>
    forceClinic || (isAssessment(referral) && isGroupService(referral)) ||
    isClinicBased(referral);

export const isClinicBased = (referral: Pick<Referral, 'serviceTypes' | 'placementType' | 'referralConsiderations'>) =>
    referral.referralConsiderations?.includes(ReferralConsideration.Clinic_based);

export const isTreatmentHourRequired = (referral: Referral) =>
    (referral.serviceTypes?.some(st => [
        ServiceType.ABA_Parent_Training,
        ServiceType.Group_ABA,
        ServiceType.Parent_Led_ABA,
        ServiceType.Social_Skills_Group,
        ServiceType.Chat,
        ServiceType.Connect,
        ServiceType.Traditional_ABA,
        ServiceType.ThreeTierOrParentTraining,
        ServiceType.ThreeTierOrParentLed,
        ServiceType.RUBI,
        ServiceType.Calibrate,
        ServiceType.Compass_Behavioral,
    ].includes(st)))
    && isTreatmentOrAssessmentWithTreatment(referral);

export const isPsychotherapyOfferRequired = (referral: Referral) =>
    (referral.serviceTypes?.some(st => [ServiceType.Psychotherapy, ServiceType.Compass_Psychotherapy].includes(st)));

export const isLanguageRequired = (referral: Pick<Referral, 'requiredLanguage'>) => Boolean(referral.requiredLanguage);

export const isAvailable = (referral: Partial<Referral>) => {
    if (!referral.status) {
        return false;
    }
    return [
        ReferralStatus.Available,
        ReferralStatus.Provider_Request,
    ].includes(referral.status);
}

export const isPreferred = (referral: Partial<Referral> | null) => {
    return (referral?.myProviderPreferences ?? []).length > 0;
}

// BHPNPP-1944
// group 4
export function isAGroupCombo(serviceTypes: ServiceType[]): boolean {
    if (serviceTypes.length < 2) {
        return false;
    }

    const combo = [
        [ServiceType.ABA_Parent_Training, ServiceType.Social_Skills_Group],
        [ServiceType.Parent_Led_ABA, ServiceType.Social_Skills_Group],
        [ServiceType.Parent_Led_ABA, ServiceType.Group_ABA]
    ];
    return (combo.some((sts) => difference(sts, serviceTypes).length === 0));
}
